import { useMutation, useQuery } from "@apollo/client";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { Box, Button, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GenericCommentStream } from "components/Comments/GenericCommentStream";
import { gql } from "generated-graphql";
import { CommentThreadFragment } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { MouseEvent, useMemo, useState } from "react";

const PROCESSED_MANIFEST_COMMENTS_QUERY = gql(`
  query ProcessedManifestComments($processedManifestId: ID!) {
    processedManifestComments(processedManifestId: $processedManifestId) {
      ...CommentThread
    }
  }
`);

const ADD_PROCESSED_MANIFEST_COMMENT_MUTATION = gql(`
  mutation AddProcessedManifestComment($processedManifestId: ID!, $comment: String!) {
    addProcessedManifestComment(processedManifestId: $processedManifestId, comment: $comment) {
      id
    }
  }
`);

const DELETE_PROCESSED_MANIFEST_COMMENT_MUTATION = gql(`
  mutation DeleteProcessedManifestComment($id: ID!) {
    deleteProcessedManifestComment(id: $id)
  }
`);

interface ManifestCommentsButtonProps {
  processedManifestId: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  active?: boolean;
  /**
   * Pre-fetched comment count. Pass this when the parent already has the count
   * in bulk (the Manifest Review grid batches it into its page query) so each
   * button doesn't fire its own request — a per-row query would N+1 the grid.
   * Omit it on single-button surfaces (drawer / dialogs), where the button
   * fetches its own count.
   */
  count?: number;
  /**
   * "icon" (default): the orange chat-bubble icon every existing caller wants.
   * "button": an outlined "Comments (n)" button with a leading chat bubble —
   * used only by the discrepancy dialog's header, where it sits between View
   * document and Mark blocked and has to match their icon-plus-label shape.
   */
  appearance?: "icon" | "button";
}

/**
 * Orange staff chat-bubble icon used to open the manifest comment thread. The
 * existing-comment count is rendered inside the bubble (no overflowing badge).
 * When the popover is open the icon switches to a solid fill instead of a
 * separate active chip.
 */
export function ManifestCommentsButton({
  processedManifestId,
  onClick,
  active,
  count: providedCount,
  appearance = "icon",
}: ManifestCommentsButtonProps) {
  const hasProvidedCount = providedCount !== undefined;
  // With a provided count, read cache-only (no network) so an already-loaded
  // thread still reflects live edits but we never issue a per-button request;
  // otherwise fetch our own count. Apollo cache-dedupes with the popover stream.
  const { data } = useQuery(PROCESSED_MANIFEST_COMMENTS_QUERY, {
    variables: { processedManifestId },
    skip: !processedManifestId,
    fetchPolicy: hasProvidedCount ? "cache-only" : "cache-and-network",
  });
  const count = data?.processedManifestComments?.length ?? providedCount ?? 0;
  const hasComments = count > 0;
  // Orange only signals "there's something here"; an empty thread reads as a
  // muted light-gray affordance so it doesn't compete with the active chips.
  const iconColor = hasComments ? "warning.main" : "action.disabled";

  if (appearance === "button") {
    // Same size, variant, and leading icon as its two siblings in the
    // discrepancy dialog's header (View document, Mark blocked) so the three
    // read as one row. The count is already in the label here, so the icon
    // appearance's in-bubble digit would just repeat it.
    return (
      <Tooltip title="Staff comments">
        <Button
          size="small"
          variant="outlined"
          startIcon={<ChatBubbleOutlineIcon />}
          onClick={onClick}
          aria-label="Staff comments"
        >
          {hasComments ? `Comments (${count})` : "Comments"}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Staff comments">
      <IconButton
        size="small"
        color={hasComments ? "warning" : "default"}
        onClick={onClick}
        aria-label="Staff comments"
        sx={{ "&:hover": { bgcolor: "action.hover" } }}
      >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          {/* Default (medium) size so the icon + hover circle match the
              neighboring close/edit toolbar icons. */}
          {active ? (
            <ChatBubbleIcon sx={{ color: iconColor }} />
          ) : (
            <ChatBubbleOutlineIcon sx={{ color: iconColor }} />
          )}
          {count > 0 && (
            <Typography
              component="span"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                // Nudge up so the digit sits in the bubble body, clear of the tail.
                pb: "3px",
                fontSize: "0.7rem",
                fontWeight: 700,
                lineHeight: 1,
                color: active ? "warning.contrastText" : "warning.main",
              }}
            >
              {count > 9 ? "9+" : count}
            </Typography>
          )}
        </Box>
      </IconButton>
    </Tooltip>
  );
}

interface ManifestCommentsStreamProps {
  processedManifestId: string;
}

/**
 * Data layer + rendered comment stream for a single manifest. Staff-only thread
 * stored against the ProcessedManifest (isStaffOnly comments). Embedded in the
 * detail-drawer header popover, the discrepancy dialog, and the document review
 * dialog.
 */
export function ManifestCommentsStream({ processedManifestId }: ManifestCommentsStreamProps) {
  const { user } = useCurrentUser();
  const alerts = useAlerts();

  const { data, loading, refetch } = useQuery(PROCESSED_MANIFEST_COMMENTS_QUERY, {
    variables: { processedManifestId },
    skip: !processedManifestId,
    fetchPolicy: "cache-and-network",
  });

  const [addComment, { loading: addingComment }] = useMutation(
    ADD_PROCESSED_MANIFEST_COMMENT_MUTATION
  );
  const [deleteComment, { loading: deletingComment }] = useMutation(
    DELETE_PROCESSED_MANIFEST_COMMENT_MUTATION
  );

  const comments = useMemo<CommentThreadFragment[]>(
    () => data?.processedManifestComments ?? [],
    [data?.processedManifestComments]
  );

  const onAddComment = async (message: string) => {
    if (!message.trim()) return;
    try {
      await addComment({ variables: { processedManifestId, comment: message.trim() } });
      await refetch();
    } catch (error) {
      console.error(error);
      alerts.error("Failed to add comment.");
    }
  };

  const onDeleteComment = async (commentId: string) => {
    try {
      await deleteComment({ variables: { id: commentId } });
      await refetch();
    } catch (error) {
      console.error(error);
      alerts.error("Failed to delete comment.");
    }
  };

  return (
    <GenericCommentStream
      title={undefined}
      comments={comments}
      currentUserId={user?.id}
      onAddComment={onAddComment}
      onDeleteComment={onDeleteComment}
      addLoading={addingComment}
      deleteLoading={deletingComment}
      placeholder="Leave a staff comment"
      buttonText="Comment"
      emptyStateMessage={loading ? "Loading comments…" : "No staff comments yet."}
      showDeleteButton
    />
  );
}

interface ManifestCommentsPopoverProps {
  processedManifestId: string;
  /** Pre-fetched comment count (see ManifestCommentsButton); avoids a per-row query. */
  count?: number;
  /** See ManifestCommentsButton — defaults to the icon every other caller wants. */
  appearance?: "icon" | "button";
}

/**
 * Self-contained staff comments affordance: the orange chat-bubble button (with
 * its count) plus the popover thread, wired to their own anchor state. This is
 * the single shared implementation used by every surface that exposes manifest
 * comments — the detail-drawer header, the discrepancy dialog, the document
 * review dialog, and the Manifest Review queue's Comments column — so the icon,
 * width, and placement can't drift between them.
 */
export function ManifestCommentsPopover({
  processedManifestId,
  count,
  appearance,
}: ManifestCommentsPopoverProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <ManifestCommentsButton
        processedManifestId={processedManifestId}
        count={count}
        appearance={appearance}
        // stopPropagation so opening the thread from a data-grid cell (the
        // Manifest Review queue's Comments column) doesn't also trigger the
        // row's onClick. Harmless on the dialog/drawer surfaces.
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        active={!!anchor}
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 450, maxWidth: "90vw", p: 2 } } }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Staff comments
        </Typography>
        <ManifestCommentsStream processedManifestId={processedManifestId} />
      </Popover>
    </>
  );
}
